import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAgentSessions } from "../../api/queries";
import type { AgentSession, Project } from "../../api/types";
import { DirtyBadge, StatusBadge } from "../bonnie/BonnieSessionsPage";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EmptyState } from "../../components/AsyncStates";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { shouldAutoLaunchDevelop } from "../develop/launchParams";
import { StartDevelopDialog } from "../develop/StartDevelopDialog";

const COLUMNS: DataTableColumn<AgentSession>[] = [
  { key: "label", header: "Session", cell: (s) => s.label, className: "data-table__primary" },
  { key: "status", header: "Status", cell: (s) => <StatusBadge status={s.status} /> },
  {
    key: "branch",
    header: "Branch",
    cell: (s) => (s.branch ? <code className="bonnie-branch">{s.branch}</code> : "—"),
    className: "data-table__secondary",
  },
  { key: "dirty", header: "Worktree", cell: (s) => <DirtyBadge session={s} /> },
  {
    key: "changeSummary",
    header: "What changed",
    cell: (s) => s.changeSummary || (s.dirty === false ? "No changes" : "—"),
    className: "data-table__secondary",
  },
  {
    key: "createdBy",
    header: "Started by",
    cell: (s) => s.createdBy || "—",
    className: "data-table__secondary",
  },
];

/**
 * The Sessions tab (IA slice 3, ui-information-architecture.md §3): this project's
 * develop (corpus-authoring) sessions, plus the "Develop with an agent" launcher that
 * used to live only on `CorpusPage`. That launcher (`StartDevelopDialog`) is now mounted
 * TWICE — here and on `CorpusPage` — deliberately: one component, two entry points, never
 * a second hand-copied dialog (A-5).
 *
 * Filtering: `useAgentSessions` has no server-side `projectId` filter today (only `local`
 * does, mirroring `BonnieSessionsPage`'s own client-side fallback) — this list fetches
 * every session and filters to `s.projectId === project.id` in the browser. That is
 * ALSO the honest degrade path if the server-side `AgentSession.projectId` field this tab
 * depends on hasn't landed yet: sessions simply won't carry it, the filter matches none,
 * and the empty state below renders truthfully rather than a fabricated list.
 */
export function ProjectSessionsTab({ project, canManage }: { project: Project; canManage: boolean }) {
  const [, navigate] = useLocation();
  const paginator = usePaginator(project.id);
  const { data, isPending, isError, error, refetch, isFetching } = useAgentSessions({
    cursor: paginator.cursor,
    local: true,
  });
  const [developOpen, setDevelopOpen] = useState(false);

  // Auto-open the launcher for a project that just landed here from Home's "Develop it"
  // card with nothing to develop yet (decision 6) — consumed at most once per mount (a
  // ref, not state, so a background re-render can't re-fire it).
  const autoLaunchConsumed = useRef(false);
  useEffect(() => {
    if (autoLaunchConsumed.current) return;
    autoLaunchConsumed.current = true;
    if (canManage && shouldAutoLaunchDevelop()) setDevelopOpen(true);
  }, [canManage]);

  const projectSessions = data ? data.items.filter((s) => s.projectId === project.id) : [];

  function handleDevelopLaunched(launched: AgentSession) {
    navigate(`/projects/${project.id}/develop/${launched.id}`);
  }

  const startButton = (
    <Button variant="primary" onClick={() => setDevelopOpen(true)}>
      Develop with an agent
    </Button>
  );

  return (
    <div className="project-sessions-tab">
      <div className="project-detail__services-header">
        <h2>Sessions</h2>
        {canManage && <div className="project-detail__services-actions">{startButton}</div>}
      </div>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading sessions…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load sessions. Check your connection and try again."
        }
        isEmpty={Boolean(data && projectSessions.length === 0)}
        empty={
          <EmptyState
            title="No develop sessions for this project yet"
            description="Start one to co-author this project's corpus in a conversation — it drafts and validates on your own machine, then you review and propose the result as a pull request."
            action={canManage ? startButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={COLUMNS}
              rows={projectSessions}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate(`/projects/${project.id}/develop/${s.id}`)}
              ariaBusy={isFetching}
            />
            <PaginatorControls
              itemCount={projectSessions.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="sessions"
            />
          </>
        )}
      </QueryStates>

      {canManage && (
        <StartDevelopDialog
          open={developOpen}
          projectId={project.id}
          projectName={project.name}
          onClose={() => setDevelopOpen(false)}
          onLaunched={handleDevelopLaunched}
        />
      )}
    </div>
  );
}
